import { id } from "@instantdb/react";
import { coerce } from "@uri/gamla";
import { apiClient, httpCommunication } from "@uri/typed-api";
import hydrate from "preact-iso/hydrate";
import { ErrorBoundary, LocationProvider, Route, Router } from "preact-iso";
import { useEffect, useMemo, useState } from "preact/hooks";
import { toast, Toaster } from "react-hot-toast";
import {
  apiDefinition,
  userApiDefinition,
} from "../../client/src/apiTyping.ts";
import { apiBaseUrl } from "../../client/src/main.ts";
import { monthlyPaymentPerLine, supportEmail } from "../../constants.ts";
import { whatsappAccountStatus } from "../../instant.schema.ts";
import { queryOnce, transact, tx, useAuth, useQuery } from "./db.ts";
import { Login } from "./login.tsx";
import {
  Button,
  ErrorMessage,
  LoadingIndication,
  QrWithTimer,
  RelatedServices,
  SecretToken,
} from "./uiComponents.tsx";
import { ApiDocs } from "./apiDocs.tsx";
import {
  LegalPage,
  PrivacyPage,
  RefundPolicyPage,
  TermsPage,
} from "./legal.tsx";
import { UserContext, useUser } from "./userContext.tsx";

if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.register("/service-worker.js");
}

const wrapperStyle = {
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  flexGrow: 1,
  width: "100%",
  height: "100vh", // Ensure wrapper fills viewport
  minHeight: 0, // Prevent flex children from growing unexpectedly
};

// Action row for account controls; wraps on narrow screens
const accountActionRowStyle = {
  display: "flex",
  gap: 8,
  flexDirection: "row",
  flexWrap: "wrap",
  marginTop: 10,
  paddingTop: 10,
  borderTop: "1px solid var(--border)",
} as const;

// Connection help section styles and content
const connectHelpStyle = {
  marginTop: 12,
  padding: 12,
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--surface-alt)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
} as const;

const adminChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "var(--surface-alt)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: "2px 8px",
} as const;

export const ConnectHelp = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={connectHelpStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ❓ Connection Help
        </div>
        <Button
          className="secondary"
          style={{ fontSize: 12, padding: "4px 10px" }}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? "Hide" : "Show"}
        </Button>
      </div>
      {isOpen && (
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <li>
            4️⃣ WhatsApp supports up to four linked devices. In WhatsApp on your
            phone: Settings → Linked Devices. If you're at the limit, disconnect
            one and try again.
          </li>
          <li>
            🔳 QR codes expire quickly. If the QR disappears or fails, refresh
            and press <strong>Send QR Code</strong> again.
          </li>
          <li>
            📨 If you can't scan a QR, or you're connecting on the same device,
            use <strong>Send Login Code</strong>.
          </li>
          <li>
            🔄 It may take a few attempts. If a try fails, trigger{" "}
            <strong>Send QR Code</strong> or <strong>Send Login Code</strong>
            {" "}
            again.
          </li>
          <li>
            📶 Make sure WhatsApp on your phone is online, the app is up to
            date, and battery saver/background restrictions aren’t blocking it.
          </li>
          <li>
            🧹 If you get stuck, use <strong>Reset</strong>{" "}
            to clear credentials, then try connecting again.
          </li>
          <li
            style={{
              marginTop: 8,
              borderTop: "1px solid var(--border)",
              paddingTop: 8,
            }}
          >
            📧 Need help with billing or account limits? Contact{" "}
            <a
              href={`mailto:${supportEmail}`}
              style={{ color: "var(--primary)" }}
            >
              {supportEmail}
            </a>
          </li>
        </ul>
      )}
    </div>
  );
};

const Header = () => (
  <div
    style={{
      padding: "10px 20px",
      backgroundColor: "#282c34",
      color: "white",
      display: "flex",
      alignItems: "center",
      gap: "24px",
    }}
  >
    <a href="/" style={{ color: "white", textDecoration: "none" }}>
      <h1 style={{ margin: 0 }}>Supergreen</h1>
    </a>
    <nav style={{ display: "flex", gap: "16px", fontSize: "14px" }}>
      <a
        href="/app"
        style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
        onMouseOver={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.opacity = "0.9";
        }}
      >
        Dashboard
      </a>
      <a
        href="/docs"
        style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
        onMouseOver={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.opacity = "0.9";
        }}
      >
        API Docs
      </a>
      <a
        href="/pricing"
        style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
        onMouseOver={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.opacity = "0.9";
        }}
      >
        Pricing
      </a>
    </nav>
  </div>
);

const footerLinkStyle = {
  color: "var(--text-muted)",
  textDecoration: "none",
  fontSize: "13px",
};

const Footer = () => (
  <footer
    style={{
      padding: "20px",
      textAlign: "center",
      display: "flex",
      justifyContent: "center",
      gap: "16px",
      flexWrap: "wrap",
      borderTop: "1px solid var(--border)",
      marginTop: "auto",
    }}
  >
    <a href="/terms" style={footerLinkStyle}>Terms</a>
    <a href="/privacy" style={footerLinkStyle}>Privacy</a>
    <a href="/refund-policy" style={footerLinkStyle}>Refunds</a>
  </footer>
);

const client = apiClient(httpCommunication(apiBaseUrl), apiDefinition);
const userClient = apiClient(httpCommunication(apiBaseUrl), userApiDefinition);

const normalizeWaId = (input: string) => {
  const s = input.trim();
  if (!s) return s;
  // If user already provided a full chat id (contains @), keep as-is
  if (s.includes("@")) return s;
  const digits = s.replace(/\D/g, "");
  return `${digits}@c.us`;
};

const getToken = async (phoneNumber: string) => {
  const { data } = await queryOnce({
    whatsappAccounts: { $: { where: { phoneNumber } } },
  });
  return data?.whatsappAccounts?.[0]?.secretToken || "";
};

const addAdmin = async (
  accountId: string,
  existingEmails: ReadonlyArray<string>,
) => {
  const raw = prompt("Enter admin email:");
  if (!raw) return;
  const email = raw.trim();
  if (!email) return;
  if (existingEmails.includes(email)) {
    toast.error("Admin already added");
    return;
  }
  const { data } = await queryOnce({
    users: { $: { where: { email } } },
  });
  const found = data?.users?.[0];
  if (!found) {
    const shouldCreate = confirm(
      "User not found. Create new user and add as admin?",
    );
    if (!shouldCreate) return;
    const newUserId = id();
    const promise = transact([
      tx.users[newUserId].update({
        email,
        lastSeen: Date.now(),
        pushSubscriptions: {},
        createdAt: Date.now(),
      }),
      tx.whatsappAccounts[accountId].link({ admins: newUserId }),
    ]);
    await toast.promise(promise, {
      loading: "Adding admin...",
      success: "Admin added",
      error: (e) => e?.message || "Failed to add admin",
    });
    return;
  }
  const promise = transact(
    tx.whatsappAccounts[accountId].link({ admins: found.id }),
  );
  await toast.promise(promise, {
    loading: "Adding admin...",
    success: "Admin added",
    error: (e) => e?.message || "Failed to add admin",
  });
};

const removeAdmin = async (
  accountId: string,
  adminId: string,
  adminEmail?: string,
) => {
  const ok = confirm(
    `Remove ${adminEmail ?? "admin"} from this WhatsApp account?`,
  );
  if (!ok) return;
  const promise = transact(
    tx.whatsappAccounts[accountId].unlink({ admins: adminId }),
  );
  await toast.promise(promise, {
    loading: "Removing admin...",
    success: "Admin removed",
    error: (e) => e?.message || "Failed to remove admin",
  });
};

const sendTest = async (fromNumber: string, userWhatsapp?: string) => {
  const raw = prompt(
    "Enter destination phone (digits or full chat id):",
    userWhatsapp || "",
  );
  if (!raw) return;
  if (!raw.includes("@") && raw.replace(/\D/g, "").startsWith("0")) {
    toast.error("Please use international format (e.g. 972...)");
    return;
  }
  const toNumber = normalizeWaId(raw);
  if (!toNumber) {
    toast.error("No phone number provided");
    return;
  }
  const token = await getToken(fromNumber);
  const promise = client({
    endpoint: "sendMessage",
    payload: {
      fromNumber,
      toNumber,
      message: "Hello World!",
      linkPreview: false,
      token,
    },
  });
  await toast.promise(promise, {
    loading: "Sending...",
    success: () => "Message sent",
    error: (e) =>
      e?.message ? `Failed: ${e.message}` : "Failed to send message",
  });
};

const reconnect = async (number: string) => {
  const token = await getToken(number);
  const promise = client({ endpoint: "reconnect", payload: { number, token } });
  await toast.promise(promise, {
    loading: "Reconnecting...",
    success: () => "Reconnect triggered",
    error: (e) =>
      e?.message ? `Failed: ${e.message}` : "Failed to trigger reconnect",
  });
};

const triggerCodeLogin = async (number: string) => {
  const token = await getToken(number);
  toast.promise(
    client({ endpoint: "triggerCodeLogin", payload: { number, token } }),
    {
      loading: "Generating code...",
      success: () => "Code login triggered",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to trigger code login",
    },
  );
};

const changeAccountName = async (phoneNumber: string) => {
  const name = prompt("Enter new WhatsApp account name:");
  if (!name || !name.trim()) return;
  await toast.promise(
    client({
      endpoint: "changeWhatsappAccountName",
      payload: {
        phoneNumber,
        name: name.trim(),
        token: await getToken(phoneNumber),
      },
    }),
    {
      loading: "Changing account name...",
      success: () => "Account name changed",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to change account name",
    },
  );
};

const changeAccountAvatar = (phoneNumber: string) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result as string;
      if (!dataUrl) {
        toast.error("Failed to read image");
        return;
      }
      const token = await getToken(phoneNumber);
      const promise = client({
        endpoint: "changeWhatsappAccountAvatar",
        payload: { phoneNumber, imageBase64: dataUrl, token },
      });
      await toast.promise(promise, {
        loading: "Uploading avatar...",
        success: () => "Avatar updated",
        error: (e) =>
          e?.message ? `Failed: ${e.message}` : "Failed to update avatar",
      });
    };
    reader.readAsDataURL(file);
  };
  input.click();
};

const editAccountName = async (
  accountId: string,
  currentName: string,
) => {
  const raw = prompt("Edit account name:", currentName);
  if (raw === null) return;
  const newName = raw.trim();
  if (newName === currentName) return;
  const promise = transact(
    tx.whatsappAccounts[accountId].update({ name: newName }),
  );
  await toast.promise(promise, {
    loading: "Saving name...",
    success: "Account name updated",
    error: (e) => e?.message || "Failed to update account name",
  });
};

const editAccountNotes = async (
  accountId: string,
  currentNotes?: string,
) => {
  const raw = prompt("Edit notes:", currentNotes ?? "");
  if (raw === null) return;
  const trimmed = raw.trim();
  const notes = trimmed === "" ? null : trimmed;
  const promise = transact(
    tx.whatsappAccounts[accountId].update({ notes }),
  );
  await toast.promise(promise, {
    loading: "Saving notes...",
    success: () => (notes ? "Notes updated" : "Notes removed"),
    error: (e) => e?.message || "Failed to update notes",
  });
};

const mergeWebhooks = (
  webhook?: string,
  webhookUrls?: string[],
): string[] => [
  ...new Set([...(webhookUrls ?? []), ...(webhook ? [webhook] : [])]),
];

const handleAddWebhook =
  (phoneNumber: string, setUrls: (fn: (prev: string[]) => string[]) => void) =>
  async () => {
    const url = prompt("Enter webhook URL:");
    if (!url) return;
    const trimmed = url.trim();
    if (!trimmed) return;
    const token = await getToken(phoneNumber);
    const promise = client({
      endpoint: "addWebhook",
      payload: { phoneNumber, url: trimmed, token },
    });
    await toast.promise(promise, {
      loading: "Adding webhook...",
      success: () => "Webhook added",
      error: (e) => e?.message || "Failed to add webhook",
    });
    setUrls((prev) => [...new Set([...prev, trimmed])]);
  };

const handleRemoveWebhook = (
  phoneNumber: string,
  url: string,
  setUrls: (fn: (prev: string[]) => string[]) => void,
) =>
async () => {
  if (!confirm(`Remove webhook?\n${url}`)) return;
  const token = await getToken(phoneNumber);
  const promise = client({
    endpoint: "removeWebhook",
    payload: { phoneNumber, url, token },
  });
  await toast.promise(promise, {
    loading: "Removing webhook...",
    success: () => "Webhook removed",
    error: (e) => e?.message || "Failed to remove webhook",
  });
  setUrls((prev) => prev.filter((u) => u !== url));
};

const webhookItemStyle = {
  fontSize: 12,
  opacity: 0.75,
  wordBreak: "break-all",
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const WebhookEditor = (
  { phoneNumber, webhook, webhookUrls }: {
    phoneNumber: string;
    webhook?: string;
    webhookUrls?: string[];
  },
) => {
  const [urls, setUrls] = useState(() => mergeWebhooks(webhook, webhookUrls));
  return (
    <div
      style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}
    >
      {urls.map((url) => (
        <div key={url} style={webhookItemStyle}>
          <span>{url}</span>
          <Button
            className="secondary"
            style={{ fontSize: 11, padding: "2px 6px" }}
            onClick={handleRemoveWebhook(phoneNumber, url, setUrls)}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        className="secondary"
        style={{ fontSize: 12, alignSelf: "flex-start" }}
        onClick={handleAddWebhook(phoneNumber, setUrls)}
      >
        + Add webhook
      </Button>
    </div>
  );
};

const useCheckoutSuccess = () => {
  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get("checkout") !== "success") return;
    toast.success("Payment received! Your capacity will update shortly.");
    const url = new URL(globalThis.location.href);
    url.searchParams.delete("checkout");
    globalThis.history.replaceState({}, "", url.toString());
  }, []);
};

const UserProfile = () => {
  const { dbUser } = useUser();
  const { user: authUser } = useAuth();
  const { data: accountsData } = useQuery({ whatsappAccounts: {} });
  useCheckoutSuccess();
  if (!dbUser) return null;
  const user = dbUser;

  const activeLines = accountsData?.whatsappAccounts?.length || 0;
  const monthlyPayment = user.monthlyPayment || 0;
  const paidCapacityLegacy = Math.floor(monthlyPayment / monthlyPaymentPerLine);
  const paidQuota = Math.max(paidCapacityLegacy, user.paidCapacity || 0);
  const createdAt = user.createdAt || Date.now();
  const trialEnd = new Date(createdAt + 30 * 24 * 60 * 60 * 1000);
  const isTrial = Date.now() < trialEnd.getTime();

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <h2 style={{ margin: 0 }}>Your Profile</h2>
      <div
        style={{
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          padding: 12,
          background: "var(--surface-alt)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ fontSize: 14, opacity: 0.85 }}>
          Email: {user.email}
        </div>
        <div
          style={{
            marginTop: 4,
            paddingTop: 8,
            borderTop: "1px solid var(--border)",
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            Billing Plan
          </div>
          {paidQuota > 0
            ? (
              <div style={{ fontSize: 13, opacity: 0.9 }}>
                Lines: <strong>{activeLines}/{paidQuota}</strong>{" "}
                (at ${monthlyPaymentPerLine}/line)
              </div>
            )
            : (
              <div style={{ fontSize: 13, opacity: 0.9 }}>
                {isTrial
                  ? (
                    <span>
                      Trial: <strong>{activeLines}/1 line</strong>
                      <br />
                      <span style={{ fontSize: 11, opacity: 0.7 }}>
                        Expires on {trialEnd.toLocaleDateString()}
                      </span>
                    </span>
                  )
                  : (
                    <span>
                      Free trial ended. Please set up payment to add lines.
                    </span>
                  )}
              </div>
            )}
        </div>
        <div style={{ marginTop: 12 }}>
          <Button
            className="primary"
            style={{ fontSize: 13, padding: "6px 16px" }}
            onClick={async () => {
              if (!authUser?.refresh_token) {
                toast.error("Not authenticated");
                return;
              }
              const checkoutClient = apiClient(
                httpCommunication(apiBaseUrl),
                { createCheckoutUrl: userApiDefinition.createCheckoutUrl },
              );
              const promise = checkoutClient({
                endpoint: "createCheckoutUrl",
                payload: { refreshToken: authUser.refresh_token },
              }).then((res) => {
                if ("error" in res) throw new Error(res.error);
                if ("url" in res) {
                  globalThis.location.href = res.url;
                } else {
                  throw new Error("No URL returned");
                }
              });
              await toast.promise(promise, {
                loading: "Redirecting to checkout...",
                success: "Redirecting...",
                error: (e) => e?.message || "Failed to create checkout session",
              });
            }}
          >
            💳 Buy / Manage Lines
          </Button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, opacity: 0.85 }}>
              Your Personal WhatsApp:
            </span>
            <span style={{ fontWeight: 600 }}>
              {user.whatsapp || "Not set"}
            </span>
            <Button
              className="secondary"
              style={{ fontSize: 12, padding: "4px 12px" }}
              onClick={async () => {
                const raw = prompt(
                  "Enter your WhatsApp number (e.g., 972501234567):",
                  user.whatsapp || "",
                );
                if (raw === null) return;
                const trimmed = raw.replace(/\D/g, "");
                if (trimmed.startsWith("0")) {
                  toast.error("Please use international format (e.g. 972...)");
                  return;
                }
                const whatsapp = trimmed === "" ? null : trimmed;
                const promise = transact(
                  tx.users[user.id].update({ whatsapp }),
                );
                await toast.promise(promise, {
                  loading: "Saving...",
                  success: () =>
                    whatsapp
                      ? "WhatsApp number updated"
                      : "WhatsApp number removed",
                  error: (e) => e?.message || "Failed to save",
                });
              }}
            >
              {user.whatsapp ? "✏ Edit" : "➕ Add"}
            </Button>
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
            ⚠️ Use this for receiving test messages and communicate with
            support, this is NOT your bot's number.
          </div>
        </div>
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: "1px solid var(--border)",
            fontSize: 13,
            opacity: 0.8,
          }}
        >
          Use the button above to add lines (${monthlyPaymentPerLine}/mo each).
          For billing questions, email{" "}
          <a
            href={`mailto:${supportEmail}`}
            style={{ color: "var(--primary)" }}
          >
            {supportEmail}
          </a>
        </div>
      </div>
    </div>
  );
};

const WhatsappAccounts = () => {
  const { dbUser } = useUser();
  const { user: authUser } = useAuth();
  const { data, error, isLoading } = useQuery({
    whatsappAccounts: {
      admins: {},
      server: {},
      staticProxy: {},
    },
  });
  if (error) return <ErrorMessage error={error} />;
  if (isLoading) return <LoadingIndication text="Loading bots..." />;
  const whatsappAccounts = data.whatsappAccounts;

  const formatStatusHistory = (
    history: Array<{ status: string; timestamp: number }> | undefined,
  ) => {
    const [showHistory, setShowHistory] = useState(false);
    if (!history || history.length === 0) return null;
    return (
      <div style={{ marginTop: 10, fontSize: 12 }}>
        <button
          type="button"
          onClick={() => setShowHistory(!showHistory)}
          style={{
            background: "none",
            border: "none",
            color: "inherit",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 12,
            padding: 0,
            opacity: 0.9,
            textDecoration: "underline",
          }}
        >
          {showHistory ? "Hide" : "Show"} Status History
        </button>
        {showHistory && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              maxHeight: "200px",
              overflowY: "auto",
              padding: "8px",
              background: "rgba(0,0,0,0.2)",
              borderRadius: "var(--radius)",
              marginTop: 6,
            }}
          >
            {[...history].reverse().map((entry, idx) => (
              <div key={idx} style={{ opacity: 0.85, fontSize: 11 }}>
                <span
                  style={{
                    color: entry.status === whatsappAccountStatus.connected ||
                        entry.status === "isLogged" ||
                        entry.status === "qrReadSuccess"
                      ? "#2e7d32"
                      : entry.status ===
                            whatsappAccountStatus.waitingForSsh ||
                          entry.status === whatsappAccountStatus.startingDocker
                      ? "#f57c00"
                      : entry.status === whatsappAccountStatus.podIsDown
                      ? "#ff5555"
                      : "#c62828",
                    fontWeight: 500,
                  }}
                >
                  {entry.status}
                </span>{" "}
                <span style={{ opacity: 0.7 }}>
                  {new Date(entry.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Your whatsapp accounts</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={async () => {
            const raw = prompt("Enter WhatsApp number (digits only):");
            if (!raw) return;
            const phoneNumber = raw.replace(/\D/g, "");
            if (
              !phoneNumber || phoneNumber.length < 10 || phoneNumber.length > 15
            ) {
              toast.error(
                "Phone number must be 10-15 digits in international format (e.g. 972501234567)",
              );
              return;
            }
            if (phoneNumber.startsWith("0")) {
              toast.error(
                "Please use international format without leading zero (e.g. 972...)",
              );
              return;
            }

            if (!authUser?.refresh_token) {
              toast.error("Not authenticated");
              return;
            }

            const promise = userClient({
              endpoint: "createAccount",
              payload: {
                refreshToken: authUser.refresh_token,
                phoneNumber,
              },
            }).then((res) => {
              if ("error" in res) throw new Error(res.error);
              return res;
            });
            await toast.promise(promise, {
              loading: "Adding account...",
              success: "Account added",
              error: (e) =>
                e?.message ||
                `Failed to add account. Please contact ${supportEmail} if the issue persists.`,
            });
          }}
        >
          ➕ Add
        </Button>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px", marginLeft: "8px" }}
          onClick={async () => {
            if (!authUser?.refresh_token) {
              toast.error("Not authenticated");
              return;
            }
            if (
              !confirm(
                "Are you sure you want to purchase a new Israeli number and provision an account for it?",
              )
            ) return;

            const promise = userClient({
              endpoint: "purchaseNumber",
              payload: {
                refreshToken: authUser.refresh_token,
              },
            }).then((res) => {
              if ("error" in res) throw new Error(res.error);
              return res;
            });
            await toast.promise(promise, {
              loading: "Purchasing number and creating account...",
              success: (res: { phoneNumber: string }) =>
                `Account created for ${res.phoneNumber}`,
              error: (e) =>
                e?.message ||
                `Failed to purchase number.`,
            });
          }}
        >
          🛒 Buy Number
        </Button>
      </div>
      <ConnectHelp />
      {whatsappAccounts.length === 0 ? <div>No bots yet.</div> : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {whatsappAccounts.map((b) => {
            const lastStatus = b.statusHistory?.[b.statusHistory.length - 1]
              ?.status;
            const isBusy = !!(lastStatus && [
              "provisioningServer",
              whatsappAccountStatus.waitingForSsh,
              "startingDocker",
            ].includes(lastStatus));
            const [expanded, setExpanded] = useState(false);
            return (
              <li
                key={b.id}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  padding: 12,
                  background: "var(--surface-alt)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => setExpanded((v) => !v)}
                >
                  <span style={{ fontSize: 12, opacity: 0.5 }}>
                    {expanded ? "▼" : "▶"}
                  </span>
                  <div style={{ fontWeight: 600 }}>
                    {b.name || b.phoneNumber}
                  </div>
                  <span
                    style={{
                      color: !b.isConnected ? "#c62828" : "#2e7d32",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    {!b.isConnected ? "Disconnected" : "Connected"}
                  </span>
                  {b.isEnabled === false && (
                    <span
                      style={{
                        color: "#ff9800",
                        fontWeight: 600,
                        fontSize: 12,
                        border: "1px solid #ff9800",
                        padding: "2px 6px",
                        borderRadius: "4px",
                      }}
                    >
                      SUSPENDED
                    </span>
                  )}
                </div>
                {expanded && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 14, opacity: 0.85 }}>
                      Number: {b.phoneNumber}
                    </div>
                    <Button
                      className="secondary"
                      style={{ fontSize: 12, padding: "2px 8px", marginTop: 4 }}
                      onClick={() => editAccountName(b.id, b.name)}
                    >
                      ✏ Edit Name
                    </Button>
                    {b.secretToken && <SecretToken token={b.secretToken} />}
                    {b.lastEventTime && (
                      <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                        Last event: {new Date(b.lastEventTime).toLocaleString()}
                      </div>
                    )}
                    {formatStatusHistory(b.statusHistory)}
                    <WebhookEditor
                      phoneNumber={b.phoneNumber}
                      webhook={b.webhook}
                      webhookUrls={b.webhookUrls}
                    />
                    {b.server && (
                      <div
                        style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}
                      >
                        <div>
                          Server: {b.server.name} ({b.server.ip})
                        </div>
                        <div>Provider: {b.server.provider}</div>
                      </div>
                    )}
                    {b.staticProxy && (
                      <div
                        style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}
                      >
                        Proxy Port: {b.staticProxy.oxylabsPort}
                      </div>
                    )}
                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 12,
                        opacity: 0.85,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <span>Admins:</span>
                      {(b.admins && b.admins.length > 0)
                        ? b.admins.map((a: { id: string; email: string }) => (
                          <span style={adminChipStyle}>
                            {a.email}
                            <Button
                              className="secondary"
                              style={{ fontSize: 10, padding: "2px 6px" }}
                              onClick={() => removeAdmin(b.id, a.id, a.email)}
                            >
                              🗑 Remove
                            </Button>
                          </span>
                        ))
                        : <span>None</span>}
                      <Button
                        className="secondary"
                        style={{ fontSize: 12, padding: "4px 10px" }}
                        onClick={() =>
                          addAdmin(
                            b.id,
                            (b.admins || []).map((
                              a: { email: string },
                            ) => a.email),
                          )}
                      >
                        ➕ Add Admin
                      </Button>
                    </div>
                    <div
                      style={{
                        marginTop: 6,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          opacity: 0.85,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {b.notes ? `Notes: ${b.notes}` : "No notes"}
                      </span>
                      <Button
                        className="secondary"
                        style={{ fontSize: 10, padding: "2px 6px" }}
                        onClick={() => editAccountNotes(b.id, b.notes)}
                      >
                        {b.notes ? "✏ Edit Notes" : "➕ Add Notes"}
                      </Button>
                    </div>
                    {!b.isConnected &&
                      b.verificationCode && (
                      <div style={{ marginTop: 10 }}>
                        <QrWithTimer
                          size={180}
                          url={b.verificationCode}
                          issuedAt={coerce(b.qrIssuedAt)}
                        />
                        <div
                          style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}
                        >
                          Scan with WhatsApp on your phone
                        </div>
                      </div>
                    )}
                    {!b.isConnected &&
                      b.pairingCode && (
                      <div style={{ marginTop: 10 }}>
                        <div
                          style={{
                            fontSize: 32,
                            fontWeight: 600,
                            letterSpacing: 8,
                            padding: "20px",
                            border: "2px solid var(--border)",
                            borderRadius: "var(--radius)",
                            background: "var(--surface-alt)",
                            textAlign: "center",
                            fontFamily: "monospace",
                          }}
                        >
                          {b.pairingCode}
                        </div>
                        <div
                          style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}
                        >
                          Enter this code in WhatsApp: Settings → Linked Devices
                          → Link a Device → Link with phone number
                        </div>
                      </div>
                    )}
                    {!b.isConnected &&
                      !b.verificationCode &&
                      !b.pairingCode && (
                      <div
                        style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}
                      >
                        Waiting for QR code or pairing code...
                      </div>
                    )}
                    <div
                      style={accountActionRowStyle}
                    >
                      <Button
                        className="primary"
                        onClick={() =>
                          sendTest(b.phoneNumber, dbUser?.whatsapp)}
                      >
                        ▶ Send test
                      </Button>
                      <Button
                        className="secondary"
                        disabled={isBusy}
                        onClick={() => reconnect(b.phoneNumber)}
                      >
                        🔳 Send QR Code
                      </Button>
                      <Button
                        className="secondary"
                        disabled={isBusy}
                        onClick={() => triggerCodeLogin(b.phoneNumber)}
                      >
                        📨 Send Login Code
                      </Button>
                      <Button
                        className="secondary"
                        onClick={() => changeAccountName(b.phoneNumber)}
                      >
                        ✏️ Change Name
                      </Button>
                      <Button
                        className="secondary"
                        onClick={() => changeAccountAvatar(b.phoneNumber)}
                      >
                        🖼️ Change Avatar
                      </Button>
                      <Button
                        className="secondary"
                        disabled={isBusy}
                        onClick={async () => {
                          if (
                            !confirm(
                              "Are you sure you want to clear all credentials for this WhatsApp number? This cannot be undone.",
                            )
                          ) return;
                          const token = await getToken(b.phoneNumber);
                          const promise = client({
                            endpoint: "clearCredentials",
                            payload: { phoneNumber: b.phoneNumber, token },
                          }).then((res) => {
                            if (!res?.success) {
                              throw new Error(
                                res?.error || "Failed to clear credentials",
                              );
                            }
                            return res;
                          });
                          await toast.promise(promise, {
                            loading: "Clearing credentials...",
                            success: "Credentials cleared",
                            error: (e) =>
                              e?.message || "Failed to clear credentials",
                          });
                        }}
                      >
                        🔄 Reset
                      </Button>
                      <Button
                        className="secondary"
                        style={{
                          color: "var(--error)",
                          borderColor: "var(--error)",
                        }}
                        onClick={async () => {
                          if (
                            !confirm(
                              "Are you sure you want to DELETE this account? This will stop the server and remove all data.",
                            )
                          ) return;

                          const token = await getToken(b.phoneNumber);
                          const promise = client({
                            endpoint: "deleteAccount",
                            payload: { phoneNumber: b.phoneNumber, token },
                          });
                          await toast.promise(promise, {
                            loading: "Deleting account...",
                            success: "Account deleted",
                            error: (e) =>
                              e.message || "Failed to delete account",
                          });
                        }}
                      >
                        ❌ Delete
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const PhoneNumbers = () => {
  const { user: authUser } = useAuth();
  const { isLoading, error, data } = useQuery(
    authUser?.email
      ? { phoneNumbers: { $: { where: { "admins.email": authUser.email } } } }
      : null,
  );
  if (!authUser || isLoading) return null;
  if (error) return <div>Error loading numbers</div>;
  const phoneNumbers = data?.phoneNumbers || [];
  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Your purchased numbers</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={async () => {
            if (!authUser?.refresh_token) {
              toast.error("Not authenticated");
              return;
            }
            if (
              !confirm(
                "Are you sure you want to purchase a new Israeli number for $5/month and provision a WhatsApp account for it?",
              )
            ) return;

            const promise = userClient({
              endpoint: "purchaseNumber",
              payload: {
                refreshToken: authUser.refresh_token,
              },
            }).then((res) => {
              if ("error" in res) throw new Error(res.error);
              return res;
            });
            await toast.promise(promise, {
              loading: "Purchasing number and creating account...",
              success: (res: { phoneNumber: string }) =>
                `Account created for ${res.phoneNumber}`,
              error: (e) =>
                e?.message ||
                `Failed to purchase number.`,
            });
          }}
        >
          🛒 Buy Number ($5/mo)
        </Button>
      </div>
      {phoneNumbers.length === 0 ? <div>No numbers purchased yet.</div> : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {phoneNumbers.map((n) => (
            <li
              key={n.id}
              style={{
                padding: "16px",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                backgroundColor: "var(--surface)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                <strong>+{n.number}</strong>
                <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 8 }}>
                  (Provider: {n.provider || "unknown"})
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SignedInHome = () => (
  <div style={wrapperStyle}>
    <Header />
    <div style={{ overflowY: "auto", flex: 1 }}>
      <UserProfile />
      <PhoneNumbers />
      <WhatsappAccounts />
      <div
        style={{ display: "flex", justifyContent: "center", padding: "20px" }}
      >
        <RelatedServices />
      </div>
      <Footer />
    </div>
  </div>
);

const DocsPage = () => (
  <div style={wrapperStyle}>
    <Header />
    <ApiDocs />
    <Footer />
  </div>
);

const pricingContainerStyle = {
  maxWidth: 700,
  margin: "0 auto",
  padding: "40px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 24,
  alignItems: "center",
  textAlign: "center",
} as const;

const pricingCardStyle = {
  border: "2px solid var(--primary)",
  borderRadius: "var(--radius)",
  padding: 32,
  background: "var(--surface-alt)",
  width: "100%",
  maxWidth: 400,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  alignItems: "center",
} as const;

const PricingPage = () => (
  <div style={wrapperStyle}>
    <Header />
    <div style={pricingContainerStyle}>
      <h1 style={{ margin: 0, fontSize: "2em" }}>Pricing</h1>
      <p style={{ color: "var(--text-muted)", margin: 0, maxWidth: 500 }}>
        Simple, transparent pricing. Pay only for the lines you connect.
      </p>
      <div style={pricingCardStyle}>
        <div style={{ fontSize: "3em", fontWeight: 700 }}>
          ${monthlyPaymentPerLine}
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: "1.1em" }}>
          per line / month
        </div>
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            fontSize: 15,
            textAlign: "left",
            width: "100%",
          }}
        >
          <li>✓ Free trial to get started</li>
          <li>✓ WhatsApp & Telegram support</li>
          <li>✓ Full REST API access</li>
          <li>✓ Webhook integrations</li>
          <li>✓ Send & receive messages</li>
          <li>✓ Multi-admin support</li>
          <li>✓ Full refund within 14 days</li>
        </ul>
        <a
          href="/"
          class="button primary"
          style={{
            marginTop: 8,
            padding: "12px 32px",
            fontSize: 16,
            textDecoration: "none",
          }}
        >
          Get Started
        </a>
        <p style={{ fontSize: 13, opacity: 0.8, margin: 0 }}>
          Questions? Email{" "}
          <a
            href={`mailto:${supportEmail}`}
            style={{ color: "var(--primary)" }}
          >
            {supportEmail}
          </a>
        </p>
      </div>
    </div>
    <Footer />
  </div>
);

const routes = [
  { path: "/app", component: SignedInHome },
];

const publicRoutes = [
  { path: "/", component: Login },
  { path: "/docs", component: DocsPage },
  { path: "/legal", component: LegalPage },
  { path: "/terms", component: TermsPage },
  { path: "/privacy", component: PrivacyPage },
  { path: "/refund-policy", component: RefundPolicyPage },
  { path: "/pricing", component: PricingPage },
];

const NotFound = () => <div>Page not found :(</div>;

const UserProvider = ({ children }: { children: preact.ComponentChildren }) => {
  const { isLoading: authLoading, error: authError, user: authUser } =
    useAuth();

  const userQuery = useMemo(
    () =>
      authUser
        ? {
          users: { $: { where: { email: authUser.email ?? "" } } },
        }
        : null,
    [authUser?.email],
  );

  const { error: queryError, isLoading: queryLoading, data } = useQuery(
    userQuery,
  );

  const [created, setCreated] = useState(false);

  useEffect(() => {
    if (!authUser?.email) return;

    if (queryLoading) return;

    // If no user found and we haven't created one yet

    if ((!data?.users || data.users.length === 0) && !created) {
      console.log("Creating user for", authUser.email);

      setCreated(true); // Prevent double submission

      transact(
        tx.users[id()].update({
          lastSeen: Date.now(),

          pushSubscriptions: {},

          email: authUser.email,

          createdAt: Date.now(),
        }),
      );
    }
  }, [authUser, queryLoading, data, created]);

  const dbUser = data?.users?.[0] || null;

  useEffect(() => {
    if (dbUser) {
      transact(tx.users[dbUser.id].update({ lastSeen: Date.now() }));
    }
  }, [dbUser?.id]); // Only run when ID changes (stable)

  const isSSR = typeof window === "undefined";

  if (!isSSR && authError) return <ErrorMessage error={authError} />;

  if (!isSSR && authLoading) {
    return <LoadingIndication text="Loading auth..." />;
  }

  if (isSSR || !authUser) {
    return (
      <>
        {typeof window !== "undefined" &&
          <Toaster toastOptions={{ duration: 4000 }} />}

        <ErrorBoundary>
          <Router>
            {publicRoutes.map(({ path, component }) => (
              <Route path={path} component={component} />
            ))}

            <Route default component={Login} />
          </Router>
        </ErrorBoundary>
      </>
    );
  }

  if (queryError) {
    return <ErrorMessage error={queryError} />;
  }

  if (queryLoading || !dbUser) {
    return <LoadingIndication text="Loading your user data..." />;
  }

  return (
    <UserContext.Provider
      value={{ dbUser, isLoading: queryLoading, error: queryError }}
    >
      {children}
    </UserContext.Provider>
  );
};

const App = () => (
  <UserProvider>
    {typeof window !== "undefined" &&
      <Toaster toastOptions={{ duration: 4000 }} />}

    <ErrorBoundary>
      <Router>
        {[...routes, ...publicRoutes].map(({ path, component }) => (
          <Route path={path} component={component} />
        ))}

        <Route default component={NotFound} />
      </Router>
    </ErrorBoundary>
  </UserProvider>
);

export const App_ = () => (
  <LocationProvider>
    <App />
  </LocationProvider>
);

hydrate(<App_ />);
